import { ClientLoaderFunctionArgs, useLoaderData } from "react-router";
import CustomError from "@components/Error/CustomError";
import { MetaDescriptorProps } from "@features/seo/utils/types";
import type { Route } from "@route-types/Default/+types/DefaultPage";
import ArticleWrapper from "@routes/Article/ArticleWrapper";
import {
  fetchDataPreview,
  validateURLPath,
} from "@routes/Default/DefaultDataHandler.server";
import { DefaultPageContext } from "@routes/Default/utils/types";
import KeywordPageContent from "@routes/Keyword/KeywordPageContent";
import SectionPageContent from "@routes/Section/SectionPageContent";
import StoryThreadPage from "@routes/StoryThread/StoryThreadPage";
import { fetchContext, ResponseType } from "@sphtech/web2-core/ssr";
import { createMetaTags } from "@sphtech/web2-seo-schema";
import { Data } from "@typings/Cue";
import { getEnvironment } from "@util/constant";
import { getEnvironmentDomain } from "@util/helpers";
import { HttpStatusCode } from "axios";

/** This is the main entry point component for /:path. */
const DefaultPage = (): React.ReactElement => {
  const context = useLoaderData<DefaultPageContext>();

  // StatusCode is not 200
  if (context && context.statusCode !== HttpStatusCode.Ok) {
    return <CustomError statusCode={context.statusCode} />;
  }

  // Check if payload is missing
  if (typeof context?.payload === "undefined") {
    return <CustomError statusCode={HttpStatusCode.NotFound} />;
  }

  if (typeof context.payload !== "string" && context.payload.kind === "art") {
    const { data: articleData, schema } = context.payload;

    return <ArticleWrapper data={articleData} schema={schema} />;
  }

  if (
    typeof context.payload !== "string" &&
    context.payload.kind === "storythread"
  ) {
    return (
      <StoryThreadPage
        data={context.payload.data}
        gaProps={context.payload.gaProps}
      />
    );
  }

  if (
    typeof context.payload !== "string" &&
    context.payload.kind === "keyword"
  ) {
    return (
      <KeywordPageContent
        {...context.payload.data}
        gaProps={context.payload.gaProps}
      />
    );
  }

  if (
    typeof context.payload !== "string" &&
    context.payload.kind === "section"
  ) {
    return (
      <SectionPageContent
        {...context.payload.data}
        gaProps={context.payload.gaProps}
      />
    );
  }

  return <CustomError statusCode={HttpStatusCode.NotFound} />;
};

export default DefaultPage;

export const meta = ({ loaderData }: Route.MetaArgs): MetaDescriptorProps[] => {
  const context = loaderData as DefaultPageContext;
  if (context.type !== ResponseType.SUCCESS) return [];

  const data = context.payload;
  if (typeof data === "string") return [];
  if (!data) return [];
  if (!data.metaTags) return [];

  return createMetaTags(data.metaTags);
};

export const loader = async ({ request, context }: Route.LoaderArgs) => {
  const data = context.previewData as Data | undefined;
  if (data?.context) return await fetchDataPreview(data.context);

  const url = new URL(request.url, `http://localhost`);
  const path = url.pathname;
  const envDomain = getEnvironmentDomain(getEnvironment());
  const absolutePath = `${envDomain}${path}`;

  return await validateURLPath(absolutePath);
};

export const clientLoader = async (args: ClientLoaderFunctionArgs) => {
  const result = (await fetchContext(args)) as { context?: DefaultPageContext };

  if (!result.context) return await args.serverLoader();

  return result.context;
};
